import { useState } from "react"
import { useForm } from "react-hook-form"
import { Mail, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api } from "@/lib/api"
import { inputClass } from "@/lib/styleClasses"

interface Props {
    open: boolean
    onClose: () => void
}

export const FindMyBookingDialog = ({ open, onClose }: Props) => {
    const { t } = useTranslation()
    const [submitted, setSubmitted] = useState(false)

    const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<{ email: string }>()

    const handleClose = () => {
        reset()
        setSubmitted(false)
        onClose()
    }

    const onSubmit = async (data: { email: string }) => {
        await api.post("/guest/find", { email: data.email })
        setSubmitted(true)
    }

    if (!open) return null

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={handleClose}
        >
            <div className="absolute inset-0 bg-black/50" />
            <div
                className="relative bg-white rounded-[40px] shadow-2xl w-full max-w-md p-8"
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={handleClose}
                    className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors"
                >
                    <X className="size-5" />
                </button>

                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-brand/10 rounded-2xl p-3">
                        <Mail className="size-5 text-brand" />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-slate-900">{t("guest.recover.dialogTitle")}</h2>
                        <p className="text-sm text-slate-500 font-bold">{t("guest.recover.dialogSubtitle")}</p>
                    </div>
                </div>

                {submitted ? (
                    <div className="space-y-4">
                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                            <p className="text-slate-700 font-bold text-sm leading-relaxed">
                                {t("guest.recover.successMessage")}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="w-full bg-slate-100 text-slate-600 font-bold rounded-[28px] py-3 px-6 hover:bg-slate-200 transition-all active:scale-95"
                        >
                            {t("guest.recover.close")}
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div>
                            <label className="block text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5">
                                {t("guest.recover.email")}
                            </label>
                            <input
                                type="email"
                                autoComplete="email"
                                placeholder="you@example.com"
                                className={inputClass}
                                {...register("email", { required: true })}
                            />
                            {errors.email && (
                                <p className="text-red-500 text-xs font-bold mt-1">
                                    {t("guest.recover.emailRequired")}
                                </p>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-brand text-white font-black rounded-[28px] py-3 px-6 shadow-xl hover:bg-brand-dark active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? t("guest.recover.sending") : t("guest.recover.send")}
                        </button>
                    </form>
                )}
            </div>
        </div>
    )
}
